import React, { useState, useCallback, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  REGISTER_MUTATION, LOGIN_MUTATION, LOGIN_WITH_GOOGLE_MUTATION, ME_QUERY,
  REQUEST_OTP_MUTATION, VERIFY_OTP_MUTATION, RESET_PIN_WITH_OTP_MUTATION, IS_FEATURE_ENABLED_QUERY,
  REQUEST_PIN_RESET_MUTATION, RESET_PIN_WITH_ADMIN_CODE_MUTATION,
} from '../graphql';
import GoogleSignInButton from './GoogleSignInButton';
import { COUNTRY_PRESETS, SUPPORTED_LOCALES } from '../i18n';
import i18n from '../i18n';
import { CURRENCY_OPTIONS } from '../utils/currency';

function ageToGroup(age) {
  if (!age) return 'ADULTS';
  if (age <= 12) return 'KIDS';
  if (age <= 17) return 'TEENS';
  if (age <= 59) return 'ADULTS';
  return 'SENIORS';
}

const themePreview = {
  KIDS:    '🎨 Colourful fun mode will be applied!',
  TEENS:   '✨ Modern theme will be applied!',
  ADULTS:  '💼 Standard professional theme',
  SENIORS: '🔎 Large text accessible mode will be applied!',
};

// Popular countries shown as quick-select buttons; full list available as fallback
const COUNTRY_OPTIONS = [
  { code: 'IN', label: '🇮🇳 India' },
  { code: 'GB', label: '🇬🇧 UK' },
  { code: 'US', label: '🇺🇸 US' },
  { code: 'CA', label: '🇨🇦 Canada' },
  { code: 'AU', label: '🇦🇺 Australia' },
  { code: 'SG', label: '🇸🇬 Singapore' },
  { code: 'NG', label: '🇳🇬 Nigeria' },
  { code: 'AE', label: '🇦🇪 UAE' },
  { code: 'ZA', label: '🇿🇦 South Africa' },
  { code: 'FR', label: '🇫🇷 France' },
  { code: 'DE', label: '🇩🇪 Germany' },
  { code: 'BR', label: '🇧🇷 Brazil' },
];

const JOIN_AS_OPTIONS = [
  {
    value: 'USER',
    icon: '🎯',
    label: 'Hobby Member',
    desc: 'Find groups, connect with people who share your interests, join live sessions',
  },
  {
    value: 'EXPERT',
    icon: '🎓',
    label: 'Expert / Coach',
    desc: 'Offer skill sessions, coaching or free charity help. Build your reputation, get rewarded',
  },
  {
    value: 'SELLER',
    icon: '🛍️',
    label: 'Market Seller',
    desc: 'Sell hobby products, kits and digital resources to community groups with discount coupons',
  },
];

// ── Shared PIN rules (mirrored from server/phone.js for instant feedback; server is authoritative) ──
const COMMON_PINS = new Set(['123456','654321','111111','000000','121212','112233','123123','696969','159753','789456','147258','102030']);
function pinProblem(pin, phone = '') {
  if (!/^\d*$/.test(pin)) return 'Digits only';
  if (pin.length !== 6) return pin.length ? 'Your PIN must be exactly 6 digits' : '';
  if (COMMON_PINS.has(pin)) return 'That PIN is too common — please choose another';
  if (/^(\d)\1{5}$/.test(pin)) return 'Your PIN cannot be the same digit repeated';
  let up = true, down = true;
  for (let i = 1; i < pin.length; i++) {
    const d = pin.charCodeAt(i) - pin.charCodeAt(i - 1);
    if (d !== 1) up = false;
    if (d !== -1) down = false;
  }
  if (up || down) return 'Your PIN cannot be consecutive digits';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length >= 6) {
    const reversed = [...digits].reverse().join('');
    if (digits.includes(pin) || reversed.includes(pin)) return 'Your PIN cannot be part of your mobile number';
  }
  return '';
}

const PIN_INPUT_STYLE = { letterSpacing: 8, fontSize: 20, textAlign: 'center', fontWeight: 700 };

// ── Mobile number + 6-digit PIN ─────────────────────────────────────────────────
// The path that works with no email and no SMS provider: a number plus a self-chosen PIN.
function PinAuthPanel({ mode, countryCode, referralCode, emailOtpEnabled, onAuthenticated, onForgotPin }) {
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [name, setName] = useState('');
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [error, setError] = useState('');

  const [register, { loading: registering }] = useMutation(REGISTER_MUTATION);
  const [loginMut, { loading: loggingIn }] = useMutation(LOGIN_MUTATION);
  const isRegister = mode === 'register';
  const problem = isRegister ? pinProblem(pin, phone) : '';
  const mismatch = isRegister && confirmPin.length === 6 && pin !== confirmPin;

  const submit = async () => {
    setError('');
    try {
      if (isRegister) {
        if (problem) { setError(problem); return; }
        if (pin !== confirmPin) { setError('Both PINs must match'); return; }
        const { data } = await register({ variables: {
          name: name.trim() || `Member ${phone.replace(/\D/g, '').slice(-4)}`,
          phone, pin,
          recoveryEmail: recoveryEmail.trim() || null,
          country: countryCode === 'IN' ? 'India' : null,
          referralCode: referralCode?.trim() || null,
        } });
        onAuthenticated(data.register);
      } else {
        const { data } = await loginMut({ variables: { phone, pin } });
        onAuthenticated(data.login);
      }
    } catch (e) { setError(e.message); }
  };

  const canSubmit = phone.replace(/\D/g, '').length >= 8 && pin.length === 6
    && (!isRegister || (!problem && confirmPin.length === 6 && pin === confirmPin));

  return (
    <div>
      <div className="form-group">
        <label className="form-label">📱 Mobile number</label>
        <input className="input" type="tel" autoComplete="tel" inputMode="tel"
          placeholder="e.g. 98765 43210" value={phone} onChange={e => setPhone(e.target.value)} />
        {countryCode === 'IN' && (
          <p style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4 }}>Indian numbers can be typed without +91.</p>
        )}
      </div>

      {isRegister && (
        <div className="form-group">
          <label className="form-label">Your name (optional)</label>
          <input className="input" placeholder="What should we call you?" value={name} onChange={e => setName(e.target.value)} />
        </div>
      )}

      <div className="form-group">
        <label className="form-label">{isRegister ? 'Choose a 6-digit PIN' : '6-digit PIN'}</label>
        <input className="input" inputMode="numeric" autoComplete="off" maxLength={6} type="password"
          placeholder="••••••" value={pin}
          onChange={e => setPin(e.target.value.replace(/\D/g, ''))} style={PIN_INPUT_STYLE} />
        {isRegister && problem && (
          <p style={{ fontSize: 12, color: 'var(--danger)', marginTop: 6 }}>{problem}</p>
        )}
      </div>

      {isRegister && (
        <>
          <div className="form-group">
            <label className="form-label">Confirm your PIN</label>
            <input className="input" inputMode="numeric" autoComplete="off" maxLength={6} type="password"
              placeholder="••••••" value={confirmPin}
              onChange={e => setConfirmPin(e.target.value.replace(/\D/g, ''))} style={PIN_INPUT_STYLE} />
            {mismatch && <p style={{ fontSize: 12, color: 'var(--danger)', marginTop: 6 }}>Both PINs must match</p>}
          </div>
          <div className="form-group">
            <label className="form-label">Recovery email (optional)</label>
            <input className="input" type="email" autoComplete="email" placeholder="you@example.com"
              value={recoveryEmail} onChange={e => setRecoveryEmail(e.target.value)} />
            <p style={{ fontSize: 11, color: recoveryEmail.trim() ? 'var(--text-dim)' : 'var(--warning)', marginTop: 4 }}>
              {recoveryEmail.trim()
                ? 'Used only to reset your PIN if you forget it — never to sign in.'
                : 'Without this you’ll need to contact support if you forget your PIN.'}
            </p>
          </div>
        </>
      )}

      {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', color: 'var(--danger)', fontSize: 'var(--font-sm)', marginBottom: 14 }}>{error}</div>}

      <button type="button" className="btn btn-primary" style={{ width: '100%' }}
        onClick={submit} disabled={registering || loggingIn || !canSubmit}>
        {registering || loggingIn ? '⏳ Please wait…' : isRegister ? '🎉 Create my account' : '🔓 Sign In'}
      </button>

      {!isRegister && emailOtpEnabled && (
        <button type="button" onClick={onForgotPin}
          style={{ display: 'block', margin: '12px auto 0', background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 600, fontSize: 'var(--font-sm)', fontFamily: 'inherit' }}>
          Forgot your PIN?
        </button>
      )}
    </div>
  );
}

// ── One-time code, by SMS or email ──────────────────────────────────────────────
// Two steps: enter the destination, then the 6-digit code. Doubles as the PIN-reset flow when
// `purpose` is PIN_RESET, in which case a new PIN is collected alongside the code.
function OtpAuthPanel({ mode, countryCode, referralCode, channels, purpose = 'AUTH', onAuthenticated }) {
  const [channel, setChannel] = useState(channels[0] || 'EMAIL');
  const [stage, setStage] = useState('destination');
  const [destination, setDestination] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [newPin, setNewPin] = useState('');
  const [masked, setMasked] = useState('');
  const [devCode, setDevCode] = useState(null);   // only set when that channel's transport is stubbed
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState('');

  const [requestOtp, { loading: requesting }] = useMutation(REQUEST_OTP_MUTATION);
  const [verifyOtp, { loading: verifying }] = useMutation(VERIFY_OTP_MUTATION);
  const [resetPin, { loading: resetting }] = useMutation(RESET_PIN_WITH_OTP_MUTATION);
  const isReset = purpose === 'PIN_RESET';

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const send = async () => {
    setError('');
    try {
      const { data } = await requestOtp({ variables: { destination, channel, purpose, country: countryCode || 'IN' } });
      setMasked(data.requestOtp.destination);
      setDevCode(data.requestOtp.devCode || null);
      setCooldown(data.requestOtp.resendInSeconds || 60);
      setStage('code');
    } catch (e) {
      const wait = e.graphQLErrors?.[0]?.extensions?.resendInSeconds;
      if (wait) { setCooldown(wait); setStage('code'); }
      setError(e.message);
    }
  };

  const submitCode = async () => {
    setError('');
    try {
      if (isReset) {
        const problem = pinProblem(newPin);
        if (problem) { setError(problem); return; }
        const { data } = await resetPin({ variables: { destination, code, newPin } });
        onAuthenticated(data.resetPinWithOtp);
        return;
      }
      const { data } = await verifyOtp({ variables: {
        destination, channel, code,
        name: name.trim() || null,
        country: countryCode === 'IN' ? 'India' : null,
        referralCode: referralCode?.trim() || null,
      } });
      onAuthenticated(data.verifyOtp);
    } catch (e) { setError(e.message); }
  };

  const busy = requesting || verifying || resetting;
  const isPhone = channel === 'SMS';

  return (
    <div>
      {channels.length > 1 && stage === 'destination' && (
        <div style={{ display: 'flex', gap: 6, padding: 4, background: 'var(--surface2)', borderRadius: 'var(--radius-sm)', marginBottom: 14 }}>
          {channels.map(c => (
            <button key={c} type="button" onClick={() => { setChannel(c); setDestination(''); setError(''); }}
              style={{ flex: 1, padding: '7px 10px', borderRadius: 'calc(var(--radius-sm) - 2px)', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12, fontFamily: 'inherit',
                background: channel === c ? 'var(--surface)' : 'transparent',
                color: channel === c ? 'var(--text)' : 'var(--text-muted)',
                boxShadow: channel === c ? '0 1px 4px rgba(0,0,0,0.12)' : 'none' }}>
              {c === 'SMS' ? '📱 By SMS' : '✉️ By email'}
            </button>
          ))}
        </div>
      )}

      {stage === 'destination' ? (
        <>
          <div className="form-group">
            <label className="form-label">
              {isReset ? '✉️ Your recovery email' : isPhone ? '📱 Mobile number' : '✉️ Email address'}
            </label>
            <input className="input" type={isPhone ? 'tel' : 'email'} inputMode={isPhone ? 'tel' : 'email'}
              autoComplete={isPhone ? 'tel' : 'email'}
              placeholder={isPhone ? 'e.g. 98765 43210' : 'you@example.com'}
              value={destination} onChange={e => setDestination(e.target.value)} />
            <p style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4 }}>
              We’ll send you a 6-digit code. It expires in 5 minutes.
            </p>
          </div>
          {mode === 'register' && !isReset && (
            <div className="form-group">
              <label className="form-label">Your name (optional)</label>
              <input className="input" placeholder="What should we call you?" value={name} onChange={e => setName(e.target.value)} />
            </div>
          )}
        </>
      ) : (
        <>
          <div className="form-group">
            <label className="form-label">Enter the code sent to {masked}</label>
            <input className="input" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
              placeholder="••••••" value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))} style={PIN_INPUT_STYLE} />
          </div>
          {isReset && (
            <div className="form-group">
              <label className="form-label">Choose a new 6-digit PIN</label>
              <input className="input" inputMode="numeric" autoComplete="off" maxLength={6} type="password"
                placeholder="••••••" value={newPin}
                onChange={e => setNewPin(e.target.value.replace(/\D/g, ''))} style={PIN_INPUT_STYLE} />
              {newPin.length === 6 && pinProblem(newPin) && (
                <p style={{ fontSize: 12, color: 'var(--danger)', marginTop: 6 }}>{pinProblem(newPin)}</p>
              )}
            </div>
          )}
          {devCode && (
            <div style={{ padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: 'var(--primary-light)', color: 'var(--primary)', fontSize: 12, fontWeight: 700, marginBottom: 12 }}>
              Dev mode — delivery not configured. Your code is <span style={{ fontFamily: 'monospace' }}>{devCode}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <button type="button" onClick={() => { setStage('destination'); setCode(''); setError(''); }}
              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 'var(--font-sm)', fontFamily: 'inherit', padding: 0 }}>
              ← Change {isPhone ? 'number' : 'address'}
            </button>
            <button type="button" onClick={send} disabled={cooldown > 0 || requesting}
              style={{ background: 'none', border: 'none', color: cooldown > 0 ? 'var(--text-dim)' : 'var(--primary)', cursor: cooldown > 0 ? 'default' : 'pointer', fontWeight: 700, fontSize: 'var(--font-sm)', fontFamily: 'inherit', padding: 0 }}>
              {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
            </button>
          </div>
        </>
      )}

      {error && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', color: 'var(--danger)', fontSize: 'var(--font-sm)', marginBottom: 14 }}>{error}</div>}

      <button type="button" className="btn btn-primary" style={{ width: '100%' }}
        onClick={stage === 'destination' ? send : submitCode}
        disabled={busy || (stage === 'destination'
          ? (isPhone ? destination.replace(/\D/g, '').length < 8 : !/^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(destination))
          : code.length < 6 || (isReset && (newPin.length !== 6 || !!pinProblem(newPin))))}>
        {busy ? '⏳ Please wait…'
          : stage === 'destination' ? '📩 Send code'
          : isReset ? '🔑 Reset my PIN' : '🔓 Verify & continue'}
      </button>
    </div>
  );
}

// ── Forgotten PIN with no recovery email ────────────────────────────────────────
// The last way back into an account that has only a phone and a PIN. Two stages, because the code
// travels out of band: the user raises a request, an admin approves it and reads them a code, and
// the user comes back here to spend it on a new PIN. An admin never sees or sets the PIN itself.
function AdminPinResetPanel({ countryCode, onAuthenticated }) {
  const [stage, setStage] = useState('request');   // request → sent → redeem
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [code, setCode] = useState('');
  const [newPin, setNewPin] = useState('');
  const [error, setError] = useState('');
  const [requestReset, { loading: requesting }] = useMutation(REQUEST_PIN_RESET_MUTATION);
  const [redeem, { loading: redeeming }] = useMutation(RESET_PIN_WITH_ADMIN_CODE_MUTATION);

  const send = async () => {
    setError('');
    if (!phone.trim()) { setError('Enter the mobile number you signed up with'); return; }
    try {
      await requestReset({ variables: { phone: phone.trim(), note: note.trim() || null } });
      setStage('sent');
    } catch (e) { setError(e.message); }
  };

  const submit = async () => {
    setError('');
    const problem = pinProblem(newPin);
    if (problem) { setError(problem); return; }
    try {
      const { data } = await redeem({ variables: { phone: phone.trim(), code: code.trim(), newPin } });
      onAuthenticated(data.resetPinWithAdminCode);
    } catch (e) { setError(e.message); }
  };

  return (
    <div>
      {stage === 'request' && (
        <>
          <p style={{ fontSize: 'var(--font-sm)', color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.7 }}>
            Ask a platform admin to reset your PIN. They'll check who you are and give you a one-time
            code — you choose the new PIN yourself, and nobody else ever sees it.
          </p>
          <div className="form-group">
            <label className="form-label">Your registered mobile number</label>
            <input className="input" type="tel" placeholder="e.g. 9876543210" value={phone} onChange={e => setPhone(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Anything that helps them identify you (optional)</label>
            <input className="input" placeholder="e.g. I'm the admin of the Teej group" value={note} onChange={e => setNote(e.target.value)} />
          </div>
          {error && <div style={{ color: 'var(--danger)', fontSize: 'var(--font-sm)', marginBottom: 10 }}>{error}</div>}
          <button className="btn btn-primary" style={{ width: '100%' }} disabled={requesting} onClick={send}>
            {requesting ? 'Sending…' : '🔑 Ask an admin to reset my PIN'}
          </button>
          <button type="button" onClick={() => setStage('redeem')}
            style={{ display: 'block', margin: '12px auto 0', background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: 'var(--font-sm)', fontFamily: 'inherit', fontWeight: 600 }}>
            Already have a code? Enter it →
          </button>
        </>
      )}

      {stage === 'sent' && (
        <div style={{ textAlign: 'center', padding: '10px 0' }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>📨</div>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Request sent</div>
          <p style={{ fontSize: 'var(--font-sm)', color: 'var(--text-muted)', lineHeight: 1.7, marginBottom: 16 }}>
            An admin will check it and give you a one-time code. Come back here once you have it.
          </p>
          <button className="btn btn-primary btn-sm" onClick={() => setStage('redeem')}>I have my code</button>
        </div>
      )}

      {stage === 'redeem' && (
        <>
          <p style={{ fontSize: 'var(--font-sm)', color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.7 }}>
            Enter the code the admin gave you, then choose a new 6-digit PIN.
          </p>
          <div className="form-group">
            <label className="form-label">Your registered mobile number</label>
            <input className="input" type="tel" placeholder="e.g. 9876543210" value={phone} onChange={e => setPhone(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">One-time code from the admin</label>
            <input className="input" inputMode="numeric" maxLength={6} placeholder="6 digits" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} />
          </div>
          <div className="form-group">
            <label className="form-label">Your new 6-digit PIN</label>
            <input className="input" inputMode="numeric" maxLength={6} type="password" placeholder="••••••" value={newPin} onChange={e => setNewPin(e.target.value.replace(/\D/g, ''))} />
          </div>
          {error && <div style={{ color: 'var(--danger)', fontSize: 'var(--font-sm)', marginBottom: 10 }}>{error}</div>}
          <button className="btn btn-primary" style={{ width: '100%' }} disabled={redeeming} onClick={submit}>
            {redeeming ? 'Setting your PIN…' : '✅ Set my new PIN'}
          </button>
          <button type="button" onClick={() => { setStage('request'); setError(''); }}
            style={{ display: 'block', margin: '12px auto 0', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 'var(--font-sm)', fontFamily: 'inherit' }}>
            ← I don't have a code yet
          </button>
        </>
      )}
    </div>
  );
}

export default function AuthModal({ mode: initialMode, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode);
  // 'email' = email + password (or Google); 'pin' = mobile number + a self-chosen 6-digit PIN, which
  // needs no SMS provider at all; 'otp' = a one-time code by whichever channel is enabled;
  // 'reset' = the forgot-PIN flow.
  const [authMethod, setAuthMethod] = useState('email');
  // Steps: 1 = account details, 2 = location, 3 = role intent (register only)
  const [step, setStep] = useState(1);
  const pendingRole = sessionStorage.getItem('pendingRole');
  const [joinAs, setJoinAs] = useState(pendingRole === 'expert' ? 'EXPERT' : pendingRole === 'seller' ? 'SELLER' : 'USER');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [form, setForm] = useState({
    name:'', email:'', password:'', age:'',
    city:'', country:'India', building:'', neighborhood:'',
    postalCode:'', timezone:'Asia/Kolkata',
    currency:'INR', locale:'en-IN', language:'en',
    selectedCountryCode: 'IN',
    referralCode: new URLSearchParams(window.location.search).get('ref') || '',
  });
  const [error, setError] = useState('');
  const { login, updateCurrentUser } = useAuth();
  const set = (k,v) => setForm(f => ({...f,[k]:v}));

  const [register, {loading:rL}] = useMutation(REGISTER_MUTATION);
  const [loginMut, {loading:lL}] = useMutation(LOGIN_MUTATION);
  const [googleLoginMut] = useMutation(LOGIN_WITH_GOOGLE_MUTATION);

  // Which one-time-code channels are actually live. Both ship OFF (SMS waits on TRAI/DLT, email on
  // SES domain verification), so by default the code option isn't offered at all — mobile + PIN is
  // the path that always works.
  const { data: smsFlag } = useQuery(IS_FEATURE_ENABLED_QUERY, { variables: { key: 'mobile_otp_enabled' }, fetchPolicy: 'cache-first' });
  const { data: emailFlag } = useQuery(IS_FEATURE_ENABLED_QUERY, { variables: { key: 'email_otp_enabled' }, fetchPolicy: 'cache-first' });
  const emailOtpEnabled = !!emailFlag?.isFeatureEnabled;
  const otpChannels = [
    ...(emailOtpEnabled ? ['EMAIL'] : []),
    ...(smsFlag?.isFeatureEnabled ? ['SMS'] : []),
  ];

  // Google sign-in / sign-up: verify the Google credential server-side, then log in.
  const handleGoogle = useCallback(async (idToken) => {
    setError('');
    try {
      const { data } = await googleLoginMut({ variables: { idToken } });
      const { token, refreshToken, user } = data.loginWithGoogle;
      login(token, refreshToken, user);
      i18n.changeLanguage(user.locale || 'en-IN');
      const role = user.role;
      onClose();
      if (joinAs === 'EXPERT' || role === 'EXPERT') navigate('/expert');
      else if (joinAs === 'SELLER' || role === 'SELLER') navigate('/seller');
      else if (role === 'ADMIN') navigate('/admin');
    } catch (e) { setError(e.message || 'Google sign-in failed'); }
  }, [googleLoginMut, login, navigate, onClose, joinAs]); // eslint-disable-line react-hooks/exhaustive-deps
  const loading = rL || lL;
  const ageGroup = ageToGroup(parseInt(form.age));

  // Every alternative panel (PIN, code, PIN-reset) lands here once the server returns a token, so
  // the post-auth routing lives in exactly one place.
  const handleAuthPayload = useCallback((payload) => {
    login(payload.token, payload.refreshToken || null, payload.user);
    i18n.changeLanguage(payload.user.locale || 'en-IN');
    const role = payload.user.role;
    onClose();
    if (joinAs === 'EXPERT' || role === 'EXPERT') navigate('/expert');
    else if (joinAs === 'SELLER' || role === 'SELLER') navigate('/seller');
    else if (role === 'ADMIN') navigate('/admin');
  }, [login, navigate, onClose, joinAs]);

  const handleCountryChange = (code) => {
    const preset = COUNTRY_PRESETS[code];
    if (!preset) return;
    setForm(f => ({ ...f, selectedCountryCode: code, country: preset.country, currency: preset.currency, locale: preset.locale, language: preset.language, timezone: preset.timezone || 'UTC', postalCode: '' }));
    i18n.changeLanguage(preset.locale);
  };

  const TOTAL_STEPS = 3;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'register' && step < TOTAL_STEPS) { setStep(s => s + 1); return; }
    setError('');
    try {
      if (mode === 'register') {
        const { data } = await register({
          variables: {
            name: form.name, email: form.email, password: form.password,
            age: form.age ? parseInt(form.age) : null,
            city: form.city || null, country: form.country || null,
            building: form.building || null, neighborhood: form.neighborhood || null,
            postalCode: form.postalCode || null,
            currency: form.currency || 'INR',
            locale: form.locale || 'en-IN',
            language: form.language || 'en',
            timezone: form.timezone || 'UTC',
            referralCode: form.referralCode?.trim() || null,
          }
        });
        login(data.register.token, data.register.refreshToken, data.register.user);
        i18n.changeLanguage(data.register.user.locale || 'en-IN');
        onClose();
        // After close, navigate to appropriate onboarding page
        if (joinAs === 'EXPERT') navigate('/expert');
        else if (joinAs === 'SELLER') navigate('/seller');
      } else {
        const { data } = await loginMut({ variables: { email: form.email, password: form.password }});
        login(data.login.token, data.login.refreshToken, data.login.user);
        i18n.changeLanguage(data.login.user.locale || 'en-IN');
        // Redirect role-based users to their dashboard
        const role = data.login.user.role;
        onClose();
        if (role === 'EXPERT') navigate('/expert');
        else if (role === 'SELLER') navigate('/seller');
        else if (role === 'ADMIN') navigate('/admin');
      }
    } catch(err) { setError(err.message); }
  };

  const countryPreset = COUNTRY_PRESETS[form.selectedCountryCode] || COUNTRY_PRESETS.IN;

  const stepLabel = mode === 'register' ? {
    1: 'Create Account',
    2: 'Your Location',
    3: 'How will you use Ledxo Hub?',
  }[step] : 'Welcome back!';

  const stepSub = mode === 'register' ? {
    1: 'Join for free — it takes 30 seconds 🚀',
    2: 'Connect with people near you (all optional)',
    3: 'Choose your role — you can change this later from your profile',
  }[step] : 'Sign in to continue';

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="modal">
        {/* Header */}
        <div style={{textAlign:'center',marginBottom:24}}>
          <svg width="40" height="40" viewBox="0 0 64 64" fill="none" style={{marginBottom:8}} xmlns="http://www.w3.org/2000/svg">
            <circle cx="32" cy="32" r="30" fill="url(#mlg)"/>
            <circle cx="32" cy="20" r="7" fill="white" opacity="0.95"/>
            <circle cx="16" cy="44" r="6.5" fill="white" opacity="0.85"/>
            <circle cx="48" cy="44" r="6.5" fill="white" opacity="0.85"/>
            <line x1="32" y1="27" x2="16" y2="37.5" stroke="white" strokeWidth="2.5" strokeOpacity="0.7"/>
            <line x1="32" y1="27" x2="48" y2="37.5" stroke="white" strokeWidth="2.5" strokeOpacity="0.7"/>
            <line x1="16" y1="44" x2="48" y2="44" stroke="white" strokeWidth="2.5" strokeOpacity="0.5"/>
            <defs><linearGradient id="mlg" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse"><stop stopColor="#2d6049"/><stop offset="1" stopColor="#38785A"/></linearGradient></defs>
          </svg>
          <h2 style={{fontSize:'var(--font-xl)',fontWeight:800,marginBottom:4}}>{stepLabel}</h2>
          <p style={{color:'var(--text-muted)',fontSize:'var(--font-sm)'}}>{stepSub}</p>
          {mode==='register' && (
            <div style={{display:'flex',justifyContent:'center',gap:6,marginTop:12}}>
              {[1,2,3].map(s=>(
                <div key={s} style={{width:28,height:4,borderRadius:2,background:step>=s?'var(--primary)':'var(--border)',transition:'background 0.2s'}} />
              ))}
            </div>
          )}
        </div>

        {/* Continue with Google (shown on login + first register step when configured) */}
        {process.env.REACT_APP_GOOGLE_CLIENT_ID && (mode==='login' || (mode==='register' && step===1)) && authMethod==='email' && (
          <div style={{ marginBottom: 18 }}>
            <GoogleSignInButton onCredential={handleGoogle} text={mode==='login' ? 'signin_with' : 'signup_with'} />
            <div style={{ display:'flex', alignItems:'center', gap:10, margin:'16px 0 2px', color:'var(--text-dim)', fontSize:12 }}>
              <div style={{ flex:1, height:1, background:'var(--border)' }} />
              or with email
              <div style={{ flex:1, height:1, background:'var(--border)' }} />
            </div>
          </div>
        )}

        {/* Method switch. Only offered at the start of a flow — switching mid-register would throw
            away whatever has already been filled in. The code option only appears when a channel is
            actually enabled, so we never show a login method that can't deliver. */}
        {(mode==='login' || step===1) && (
          <div style={{ display:'flex', gap:6, padding:4, background:'var(--surface2)', borderRadius:'var(--radius-sm)', marginBottom:16, flexWrap:'wrap' }}>
            {[
              { v:'email', label:'✉️ Email' },
              { v:'pin',   label:'📱 Mobile + PIN' },
              ...(otpChannels.length ? [{ v:'otp', label:'🔑 Use a code' }] : []),
            ].map(m => (
              <button key={m.v} type="button" onClick={()=>{ setAuthMethod(m.v); setError(''); }}
                style={{ flex:'1 1 30%', padding:'8px 10px', borderRadius:'calc(var(--radius-sm) - 2px)', border:'none', cursor:'pointer',
                  fontWeight:700, fontSize:12, fontFamily:'inherit',
                  background: authMethod===m.v ? 'var(--surface)' : 'transparent',
                  color: authMethod===m.v ? 'var(--text)' : 'var(--text-muted)',
                  boxShadow: authMethod===m.v ? '0 1px 4px rgba(0,0,0,0.12)' : 'none' }}>
                {m.label}
              </button>
            ))}
          </div>
        )}

        {authMethod === 'pin' ? (
          <PinAuthPanel
            mode={mode}
            countryCode={form.selectedCountryCode}
            referralCode={form.referralCode}
            emailOtpEnabled={emailOtpEnabled}
            onForgotPin={() => { setAuthMethod('reset'); setError(''); }}
            onAuthenticated={handleAuthPayload}
          />
        ) : authMethod === 'reset' ? (
          <div>
            <p style={{ fontSize:'var(--font-sm)', color:'var(--text-muted)', marginBottom:14 }}>
              Enter the recovery email you gave when you signed up. We’ll send a code so you can pick
              a new PIN.
            </p>
            <OtpAuthPanel
              mode="login"
              countryCode={form.selectedCountryCode}
              channels={['EMAIL']}
              purpose="PIN_RESET"
              onAuthenticated={handleAuthPayload}
            />
            <button type="button" onClick={()=>{ setAuthMethod('adminreset'); setError(''); }}
              style={{ display:'block', margin:'14px auto 0', background:'none', border:'none', color:'var(--primary)', cursor:'pointer', fontSize:'var(--font-sm)', fontFamily:'inherit', fontWeight:600 }}>
              No recovery email? Ask an admin instead →
            </button>
            <button type="button" onClick={()=>{ setAuthMethod('pin'); setError(''); }}
              style={{ display:'block', margin:'12px auto 0', background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:'var(--font-sm)', fontFamily:'inherit' }}>
              ← Back to sign in
            </button>
          </div>
        ) : authMethod === 'adminreset' ? (
          <div>
            <AdminPinResetPanel countryCode={form.selectedCountryCode} onAuthenticated={handleAuthPayload} />
            <button type="button" onClick={()=>{ setAuthMethod('pin'); setError(''); }}
              style={{ display:'block', margin:'12px auto 0', background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', fontSize:'var(--font-sm)', fontFamily:'inherit' }}>
              ← Back to sign in
            </button>
          </div>
        ) : authMethod === 'otp' ? (
          <OtpAuthPanel
            mode={mode}
            countryCode={form.selectedCountryCode}
            referralCode={form.referralCode}
            channels={otpChannels}
            onAuthenticated={handleAuthPayload}
          />
        ) : (
        <form onSubmit={handleSubmit}>

          {/* ── STEP 1: Account details ── */}
          {mode==='register' && step===1 && <>
            <div className="form-group">
              <label className="form-label">Your name</label>
              <input className="input" placeholder="e.g. Emma, Rahul or Grandpa Joe 😊" value={form.name} onChange={e=>set('name',e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label">Your age (personalises your experience)</label>
              <input className="input" type="number" min={5} max={120} placeholder="How old are you?" value={form.age} onChange={e=>set('age',e.target.value)} />
              {form.age && (
                <div style={{marginTop:6,padding:'8px 12px',background:'var(--primary-light)',borderRadius:'var(--radius-sm)',fontSize:'var(--font-sm)',color:'var(--primary)',fontWeight:600}}>
                  {themePreview[ageGroup]}
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="input" type="email" placeholder="you@example.com" value={form.email} onChange={e=>set('email',e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label">Password (at least 6 characters)</label>
              <input className="input" type="password" placeholder="••••••••" value={form.password} onChange={e=>set('password',e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label">🎁 Referral code (optional)</label>
              <input className="input" placeholder="Got a friend's code? Earn 25 bonus coins" value={form.referralCode} onChange={e=>set('referralCode',e.target.value.toUpperCase())} />
            </div>
          </>}

          {/* ── STEP 2: Location ── */}
          {mode==='register' && step===2 && <>
            <div className="form-group">
              <label className="form-label">🌍 Your country</label>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginTop:4}}>
                {COUNTRY_OPTIONS.map(c => (
                  <button key={c.code} type="button" onClick={() => handleCountryChange(c.code)}
                    style={{padding:'12px 8px',borderRadius:'var(--radius-sm)',border:`2px solid ${form.selectedCountryCode===c.code?'var(--primary)':'var(--border)'}`,background:form.selectedCountryCode===c.code?'var(--primary-light)':'transparent',color:form.selectedCountryCode===c.code?'var(--primary)':'var(--text-muted)',cursor:'pointer',fontWeight:700,fontSize:'var(--font-sm)',fontFamily:'inherit',textAlign:'center'}}>
                    {c.label}
                  </button>
                ))}
              </div>
              <div style={{marginTop:8,fontSize:'var(--font-sm)',color:'var(--text-muted)',padding:'8px 12px',background:'var(--surface2)',borderRadius:'var(--radius-sm)'}}>
                💱 Currency auto-set to <strong style={{color:'var(--text)'}}>{CURRENCY_OPTIONS.find(c=>c.value===form.currency)?.label || form.currency}</strong>
              </div>
            </div>
            <div style={{padding:'10px 14px',background:'var(--surface2)',borderRadius:'var(--radius-sm)',marginBottom:14,fontSize:'var(--font-sm)',color:'var(--text-muted)',lineHeight:1.6}}>
              📍 Helps us find people near your <strong style={{color:'var(--text)'}}>building, neighbourhood, or city</strong>. All optional.
            </div>
            <div className="form-group">
              <label className="form-label">🏢 {countryPreset.postalCodeLabel==='PIN Code'?'Society / Colony':'Building or Estate name'}</label>
              <input className="input" placeholder={countryPreset.buildingPlaceholder||'e.g. Elm Court'} value={form.building} onChange={e=>set('building',e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">🏘️ Neighbourhood / Area</label>
              <input className="input" placeholder="e.g. Downtown, Bandra West, Oak Park" value={form.neighborhood} onChange={e=>set('neighborhood',e.target.value)} />
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div className="form-group">
                <label className="form-label">🏙️ City</label>
                <input className="input" placeholder={countryPreset.cityPlaceholder?.split(',')[0].replace('e.g. ','')||'City'} value={form.city} onChange={e=>set('city',e.target.value)} />
              </div>
              {countryPreset.postalCodeLabel && (
                <div className="form-group">
                  <label className="form-label">📮 {countryPreset.postalCodeLabel}</label>
                  <input className="input" placeholder={countryPreset.postalCodePlaceholder||''} value={form.postalCode} onChange={e=>set('postalCode',e.target.value)} />
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">🗣️ Language &amp; Region</label>
              <select className="input" value={form.locale} onChange={e=>{set('locale',e.target.value);i18n.changeLanguage(e.target.value);}}>
                {SUPPORTED_LOCALES.map(l=><option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}
              </select>
            </div>
          </>}

          {/* ── STEP 3: Role / Intent ── */}
          {mode==='register' && step===3 && (
            <div style={{display:'grid',gap:12}}>
              {JOIN_AS_OPTIONS.map(opt=>(
                <button key={opt.value} type="button" onClick={()=>setJoinAs(opt.value)}
                  style={{
                    padding:'16px 18px',borderRadius:'var(--radius)',textAlign:'left',cursor:'pointer',fontFamily:'inherit',
                    border:`2px solid ${joinAs===opt.value?'var(--primary)':'var(--border)'}`,
                    background:joinAs===opt.value?'var(--primary-light)':'var(--surface2)',
                    transition:'all 0.15s',
                  }}>
                  <div style={{display:'flex',alignItems:'center',gap:12}}>
                    <span style={{fontSize:28,flexShrink:0}}>{opt.icon}</span>
                    <div>
                      <div style={{fontWeight:800,fontSize:'var(--font-base)',color:joinAs===opt.value?'var(--primary)':'var(--text)',marginBottom:3}}>
                        {opt.label}
                        {joinAs===opt.value && <span style={{marginLeft:8,fontSize:12,background:'var(--primary)',color:'#fff',padding:'1px 8px',borderRadius:99}}>Selected</span>}
                      </div>
                      <div style={{fontSize:'var(--font-sm)',color:'var(--text-muted)',lineHeight:1.4}}>{opt.desc}</div>
                    </div>
                  </div>
                </button>
              ))}
              {joinAs !== 'USER' && (
                <div style={{padding:'10px 14px',background:'rgba(99,102,241,0.08)',borderRadius:'var(--radius-sm)',border:'1px solid var(--primary)',fontSize:'var(--font-sm)',color:'var(--primary)',lineHeight:1.5}}>
                  {joinAs==='EXPERT'
                    ? '🎓 After creating your account you\'ll complete your expert profile — add your skills, availability and rates.'
                    : '🛍️ After creating your account you\'ll set up your seller profile and create discount coupons for group members.'}
                </div>
              )}

              {/* ── GDPR / CPRA consent checkboxes ── */}
              <div style={{borderTop:'1px solid var(--border)',paddingTop:16,display:'grid',gap:10}}>
                <label style={{display:'flex',gap:10,alignItems:'flex-start',cursor:'pointer',fontSize:'var(--font-sm)'}}>
                  <input type="checkbox" required checked={termsAccepted} onChange={e=>setTermsAccepted(e.target.checked)} style={{width:16,height:16,marginTop:2,flexShrink:0,accentColor:'var(--primary)'}} />
                  <span style={{color:'var(--text-muted)',lineHeight:1.5}}>
                    I agree to the <a href="/privacy" target="_blank" rel="noreferrer" style={{color:'var(--primary)'}}>Terms of Service</a> and confirm I am 13 years old or older (or registering my child with parental consent). <span style={{color:'var(--danger)'}}>*</span>
                  </span>
                </label>
                <label style={{display:'flex',gap:10,alignItems:'flex-start',cursor:'pointer',fontSize:'var(--font-sm)'}}>
                  <input type="checkbox" required checked={privacyAccepted} onChange={e=>setPrivacyAccepted(e.target.checked)} style={{width:16,height:16,marginTop:2,flexShrink:0,accentColor:'var(--primary)'}} />
                  <span style={{color:'var(--text-muted)',lineHeight:1.5}}>
                    I have read and accept the <a href="/privacy" target="_blank" rel="noreferrer" style={{color:'var(--primary)'}}>Privacy Policy</a>. I understand my data is processed to provide the Ledxo Hub service (GDPR Art. 6(1)(b)). <span style={{color:'var(--danger)'}}>*</span>
                  </span>
                </label>
                <p style={{fontSize:11,color:'var(--text-muted)',margin:0,lineHeight:1.5}}>
                  🇪🇺 GDPR · 🇺🇸 CPRA compliant. You can export or delete your data at any time from your Profile. We never sell your personal data.
                </p>
              </div>
            </div>
          )}

          {/* ── LOGIN ── */}
          {mode==='login' && <>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="input" type="email" placeholder="you@example.com" value={form.email} onChange={e=>set('email',e.target.value)} required autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input className="input" type="password" placeholder="••••••••" value={form.password} onChange={e=>set('password',e.target.value)} required />
            </div>
            <div style={{marginBottom:12,fontSize:'var(--font-sm)',color:'var(--text-muted)',padding:'10px 14px',background:'var(--surface2)',borderRadius:'var(--radius-sm)'}}>
              <strong>Experts</strong> and <strong>Sellers</strong> sign in with the same form — you'll be taken to your dashboard automatically.
            </div>
          </>}

          {error && <div style={{background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.3)',borderRadius:'var(--radius-sm)',padding:'10px 14px',color:'var(--danger)',fontSize:'var(--font-sm)',marginBottom:14}}>{error}</div>}

          <div style={{display:'flex',gap:10}}>
            {mode==='register' && step>1 && (
              <button type="button" className="btn btn-ghost" style={{flex:1}} onClick={()=>setStep(s=>s-1)}>← Back</button>
            )}
            <button type="submit" className="btn btn-primary" style={{flex:2}} disabled={loading || (mode==='register' && step===3 && (!termsAccepted || !privacyAccepted))}>
              {loading ? '⏳ Please wait…'
                : mode==='register'
                  ? step < TOTAL_STEPS ? 'Next →'
                  : joinAs==='EXPERT' ? '🎓 Create Expert Account'
                  : joinAs==='SELLER' ? '🛍️ Create Seller Account'
                  : '🎉 Create My Account!'
                : '🔓 Sign In'}
            </button>
          </div>
        </form>
        )}

        <div className="divider" />
        <p style={{textAlign:'center',fontSize:'var(--font-sm)',color:'var(--text-muted)'}}>
          {mode==='register' ? 'Already have an account?' : 'New here?'}
          {' '}
          <button onClick={()=>{setMode(mode==='register'?'login':'register');setStep(1);setError('');}}
            style={{background:'none',border:'none',color:'var(--primary)',cursor:'pointer',fontWeight:700,fontSize:'var(--font-sm)',fontFamily:'inherit'}}>
            {mode==='register' ? 'Sign in' : 'Join free — for everyone!'}
          </button>
        </p>
      </div>
    </div>
  );
}
